"use client"
import { useEffect, useState, useRef } from "react"
import styles from "./SignupSuccessOverlay.module.css"
import { actionCreateSignup } from "@/app/api/signUp/create/actions"
import type { TApiSignupCreateParams, TApiSignupCreateResponse } from "@/api/launcherApp/signup/schema"
import type { TEnrichedPersonData } from "@/app/api/enrich/email/schema"
import { makeURL } from "@nauverse/make-url"
import { config } from "@/config"
import { LAUNCHER_APP_PATH } from "@/config/defaults"
import { Modal, PasswordInput, TextInput } from "@mantine/core"
import { deleteCookie } from "cookies-next/client"
import { buildSignupPostHogEventProperties, buildSignupPostHogPersonProperties, getAliasSourceDistinctIds, normalizeSignupDistinctId, persistPendingSignupDistinctId, POSTHOG_USER_SIGNED_UP_EVENT } from "@/lib/posthog/identity"
import { usePostHog } from "posthog-js/react"
import { PHRASES, VISIBLE_ITEMS, ACTIVE_ITEM_POSITION, ITEM_DURATION, ITEM_HEIGHT, shuffleArray, PhraseItem } from "./SignupAnimationParts"

const loginURL = makeURL(config.environment.api.launcherURL ?? config.environment.api.baseURL, `${LAUNCHER_APP_PATH}/login/web`, {
  config: {
    trailingSlash: "remove",
  },
  params: {
    resource: "next",
  },
})

export function SignupSuccessOverlay({
  onSuccess,
  onError,
  createParams,
  userData,
  enrichmentData,
  opened,
  onClose,
}: {
  onSuccess?: () => void
  onError?: () => void
  createParams?: TApiSignupCreateParams
  userData?: {
    email: string
    password: string
    plan: "solo" | "team"
    company: string
    companySize?: string
    companyReps?: string
    companyCountry?: string
  }
  enrichmentData?: TEnrichedPersonData | null
  opened: boolean
  onClose: () => void
}) {
  const posthog = usePostHog()
  const isCreating = useRef(false)
  const formRef = useRef<HTMLFormElement>(null)

  // Shuffle and prepare phrases
  const shuffledPhrases = useRef(shuffleArray([...PHRASES]))

  // Current progress (0-1) for smooth animation
  const [progress, setProgress] = useState(0)

  // Current start index in the phrases array
  const [startIndex, setStartIndex] = useState(0)

  // Animation timing references
  const startTimeRef = useRef<number | null>(null)
  const animationRef = useRef<number>(0)
  const progressRef = useRef(0)

  // Create a window of phrases to display
  const visiblePhrases = Array.from({ length: VISIBLE_ITEMS + 2 }, (_, i) => {
    const phraseIndex = (startIndex + i) % shuffledPhrases.current.length
    return {
      id: `${phraseIndex}-${startIndex}`, // Unique key combining phrase index and start index
      phrase: shuffledPhrases.current[phraseIndex],
      visualPosition: i,
      isActive: i === ACTIVE_ITEM_POSITION + 1 && progress < 0.5,
      isComplete: i < ACTIVE_ITEM_POSITION + 1 || (i === ACTIVE_ITEM_POSITION + 1 && progress >= 0.5),
    }
  })

  useEffect(() => {
    // Use a stable, consistent timestamp reference
    const startAnimation = () => {
      startTimeRef.current = performance.now()
      progressRef.current = 0

      const animate = () => {
        // Get current timestamp for precise timing
        const now = performance.now()
        const elapsedSinceStart = startTimeRef.current !== null ? now - startTimeRef.current : 0

        // Calculate progress based on elapsed time and duration
        const newProgress = (elapsedSinceStart % ITEM_DURATION) / ITEM_DURATION

        // Check if we've completed a full item cycle
        const completedCycles = Math.floor(elapsedSinceStart / ITEM_DURATION)

        // Update start index if needed
        if (completedCycles > 0 && progressRef.current < 1 && newProgress >= 0 && newProgress < progressRef.current) {
          setStartIndex((prevIndex) => (prevIndex + 1) % shuffledPhrases.current.length)
        }

        // Update progress state only when it changes significantly enough
        // This reduces unnecessary re-renders
        if (Math.abs(newProgress - progressRef.current) > 0.003) {
          setProgress(newProgress)
          progressRef.current = newProgress
        }

        animationRef.current = requestAnimationFrame(animate)
      }

      animate()
    }

    startAnimation()

    return () => {
      cancelAnimationFrame(animationRef.current)
    }
  }, [])

  useEffect(() => {
    if (isCreating.current) {
      return
    }

    if (!createParams) {
      return
    }

    isCreating.current = true

    async function createSignup() {
      try {
        if (!createParams) {
          throw new Error("No create params")
        }

        let succeededFirstTime = false
        let createResult: { success: boolean; data?: TApiSignupCreateResponse } | undefined

        try {
          createResult = await actionCreateSignup(createParams)
          succeededFirstTime = true
        } catch (err) {
          //
        }

        if (!succeededFirstTime) {
          createResult = await actionCreateSignup(createParams)
        }

        if (!createResult || !createResult.success || !createResult.data?.success) {
          throw new Error("Failed to create signup")
        }

        if (!formRef.current) {
          throw new Error("No form ref")
        }

        if (!userData) {
          throw new Error("No user data")
        }

        // Delete it if it exists
        deleteCookie("ringio-signed-up", {
          httpOnly: false,
          secure: true,
          sameSite: "lax",
          domain: ".ring.io",
        })

        const signupDistinctId = normalizeSignupDistinctId(userData.email)

        if (!signupDistinctId) {
          throw new Error("Invalid signup distinct id")
        }

        const currentDistinctId = posthog.get_distinct_id()
        const aliasSourceIds = getAliasSourceDistinctIds({
          currentDistinctId,
          persistedSignupDistinctId: null,
          nextDistinctId: signupDistinctId,
        })

        aliasSourceIds.forEach((aliasSourceId) => {
          posthog.alias(signupDistinctId, aliasSourceId)
        })

        posthog.identify(signupDistinctId, buildSignupPostHogPersonProperties(userData, enrichmentData))
        persistPendingSignupDistinctId(typeof window !== "undefined" ? window.localStorage : null, signupDistinctId)

        posthog.capture(POSTHOG_USER_SIGNED_UP_EVENT, buildSignupPostHogEventProperties(userData, enrichmentData))

        formRef.current.submit()
      } catch (err) {
        onError?.()
      } finally {
        isCreating.current = false
      }
    }

    createSignup()
  }, [onSuccess, onError, createParams, userData, enrichmentData, posthog])

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      fullScreen
      radius={0}
      withCloseButton={false}
      closeOnClickOutside={false}
      closeOnEscape={false}
      lockScroll={true}
      trapFocus={true}
      classNames={{
        root: styles.modal,
        body: styles.body,
      }}
    >
      <div className={styles.overlay}>
        <div
          className={styles.container}
          style={{
            position: "relative",
            height: `${ITEM_HEIGHT * VISIBLE_ITEMS}px`,
            overflow: "hidden",
          }}
        >
          {visiblePhrases.map((item) => (
            <PhraseItem key={item.id} phrase={item.phrase} visualPosition={item.visualPosition} isActive={item.isActive} isComplete={item.isComplete} progress={progress} />
          ))}
        </div>

        <div
          style={{
            display: "none",
          }}
        >
          <form ref={formRef} method="POST" action={loginURL}>
            <TextInput readOnly name={"email"} value={userData?.email} />
            <PasswordInput readOnly name={"password"} value={userData?.password} />
          </form>
        </div>
      </div>
    </Modal>
  )
}
